/**
 * NotebookCard - Grid card for notebook list display
 *
 * Features:
 * - Dynamic icon based on content analysis
 * - Inline rename capability
 * - Delete/menu actions
 * - Processing state indicator
 *
 * @module components/NotebookCard
 */

import React, { useState, useRef, useEffect } from 'react';

import { FileText, Trash2, Edit3, MoreVertical, Loader2 } from 'lucide-react';

/**
 * Get dynamic icon based on notebook content
 * @param {Object} notebook - Notebook object
 * @returns {Object} Icon configuration
 */
export const getNotebookIcon = notebook => {
  const title = (notebook.title || '').toLowerCase();
  const files = notebook.files || [];
  const fileNames = files.map(f => (f.originalName || f.name || '').toLowerCase()).join(' ');
  const combined = `${title} ${fileNames}`;

  // Legal/Contract documents
  if (
    combined.match(
      /contract|agreement|عقد|اتفاق|التزام|قانون|legal|law|terms|conditions|liability/i
    )
  ) {
    return { icon: '📜' };
  }

  // Professional Services / Business Documents
  if (combined.match(/professional|services|template|sample|proposal|invoice|report/i)) {
    return { icon: '📋' };
  }

  // Retail/FMCG/Sales Data
  if (combined.match(/sell|sales|retail|fmcg|hyper|market|store|inventory|stock|mass|data/i)) {
    return { icon: '⚔️' };
  }

  // Financial/Accounting
  if (combined.match(/finance|financial|accounting|budget|revenue|expense|profit|loss|balance/i)) {
    return { icon: '💰' };
  }

  // Research/Academic
  if (combined.match(/research|study|analysis|thesis|paper|academic|university|نظرية/i)) {
    return { icon: '🔬' };
  }

  // HR/People
  if (combined.match(/employee|hr|human|resource|staff|team|hiring|recruitment/i)) {
    return { icon: '👥' };
  }

  // Technical/Engineering
  if (combined.match(/technical|engineering|system|software|code|api|architecture/i)) {
    return { icon: '⚙️' };
  }

  // Medical/Healthcare
  if (combined.match(/medical|health|patient|clinic|hospital|doctor|treatment/i)) {
    return { icon: '🏥' };
  }

  // Real Estate/Property
  if (combined.match(/property|real estate|lease|rent|tenant|building|apartment/i)) {
    return { icon: '🏢' };
  }

  // Default - generic notebook
  return { icon: '📓' };
};

/**
 * Format date string nicely
 */
const formatDate = dateStr => {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = dateStr => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * NotebookCard Component
 *
 * @param {Object} props
 * @param {Object} props.notebook - Notebook data object
 * @param {Function} props.onSelect - Called when card is clicked
 * @param {Function} props.onDelete - Called when delete is clicked
 * @param {Function} props.onRename - Called with (id, newTitle)
 */
const NotebookCard = ({ notebook, onSelect, onDelete, onRename }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(notebook.title || '');
  const inputRef = useRef(null);

  const filesCount = notebook.files?.length || 0;
  const vaultState =
    notebook?._vaultState?.vaultState || notebook?._vaultState?.data?.vaultState || null;
  const vaultStateNorm = String(vaultState || '').toUpperCase();
  const isReady = vaultState
    ? ['READY', 'PARTIAL_READY'].includes(vaultStateNorm)
    : notebook.isReady || notebook.status === 'ready';
  const isProcessing = vaultState ? ['INGESTING'].includes(vaultStateNorm) : notebook.status === 'processing';
  const isDraft = !isProcessing && filesCount === 0 && (vaultStateNorm === 'INIT' || notebook.status === 'created');
  const isLowQuality = vaultStateNorm === 'BLOCKED_LOW_QUALITY';
  const isError = vaultStateNorm === 'ERROR';

  // Get dynamic icon based on content
  const iconConfig = getNotebookIcon(notebook);

  // Focus input when editing
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Close menu when clicking outside
  useEffect(() => {
    if (menuOpen) {
      const handler = () => setMenuOpen(false);
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
    }
  }, [menuOpen]);

  const handleSaveTitle = () => {
    if (editTitle.trim() && editTitle !== notebook.title && onRename) {
      onRename(notebook.id, editTitle.trim());
    }
    setIsEditing(false);
  };

  return (
    <div
      onClick={!isEditing ? onSelect : undefined}
      className="group relative bg-white dark:bg-neutral-800 rounded-2xl border border-slate-200 dark:border-neutral-700 p-5 cursor-pointer hover:shadow-lg hover:border-amber-300 dark:hover:border-amber-500/50 transition-all duration-200"
    >
      {/* Action buttons - Delete + Three dots menu */}
      <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
        {/* Delete button - always visible on hover */}
        <button
          onClick={e => {
            e.stopPropagation();
            onDelete(notebook);
          }}
          className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-all"
          title="Delete notebook"
        >
          <Trash2 className="h-4 w-4" />
        </button>

        {/* Three dots menu for more options */}
        <div className="relative">
          <button
            onClick={e => {
              e.stopPropagation();
              setMenuOpen(!menuOpen);
            }}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-neutral-700 transition-all"
            title="More options"
          >
            <MoreVertical className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-neutral-800 rounded-lg border border-slate-200 dark:border-neutral-700 shadow-lg z-50 py-1 min-w-[120px]">
              <button
                onClick={e => {
                  e.stopPropagation();
                  setMenuOpen(false);
                  setEditTitle(notebook.title || '');
                  setIsEditing(true);
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 dark:text-neutral-200 hover:bg-slate-100 dark:hover:bg-neutral-700"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Rename
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dynamic Icon - no background, just the emoji */}
      <div className="mb-4">
        {isProcessing ? (
          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-amber-500">
            <Loader2 className="h-7 w-7 text-white animate-spin" />
          </div>
        ) : (
          <span className="text-5xl" role="img" aria-label="notebook icon">
            {iconConfig.icon}
          </span>
        )}
      </div>

      {/* Title (editable) */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editTitle}
          onChange={e => setEditTitle(e.target.value)}
          onClick={e => e.stopPropagation()}
          onBlur={handleSaveTitle}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSaveTitle();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          className="w-full px-2 py-1 mb-1 text-base font-semibold bg-white dark:bg-neutral-700 border-2 border-amber-400 rounded outline-none"
        />
      ) : (
        <h3 className="font-semibold text-slate-900 dark:text-white mb-1 truncate">
          {notebook.title || 'Untitled Notebook'}
        </h3>
      )}

      {/* Stats */}
      <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-neutral-400">
        <span className="flex items-center gap-1">
          <FileText className="h-3.5 w-3.5" />
          {filesCount} source{filesCount !== 1 ? 's' : ''}
        </span>
        {isProcessing ? (
          <span className="text-amber-500 text-xs">Processing...</span>
        ) : isDraft ? (
          <span className="text-slate-400 text-xs">Draft (no sources)</span>
        ) : isLowQuality ? (
          <span className="text-rose-500 text-xs">Low quality</span>
        ) : isError ? (
          <span className="text-rose-500 text-xs">Needs attention</span>
        ) : isReady ? (
          <span className="text-emerald-500 text-xs">Ready</span>
        ) : null}
      </div>

      {/* Files preview */}
      {filesCount > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {notebook.files.slice(0, 3).map((file, i) => (
            <span
              key={i}
              className="px-2 py-0.5 bg-slate-100 dark:bg-neutral-700 text-xs text-slate-600 dark:text-neutral-300 rounded truncate max-w-[120px]"
            >
              {file.originalName || file.name || 'file'}
            </span>
          ))}
          {filesCount > 3 && (
            <span className="px-2 py-0.5 bg-slate-100 dark:bg-neutral-700 text-xs text-slate-500 rounded">
              +{filesCount - 3} more
            </span>
          )}
        </div>
      )}

      {/* Enhanced dates - Created & Last Modified */}
      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-neutral-700">
        <div className="flex items-center justify-between text-xs text-slate-400 dark:text-neutral-500">
          <span
            title={`Created: ${formatDate(notebook.createdAt)} ${formatTime(notebook.createdAt)}`}
          >
            Created: {formatDate(notebook.createdAt)}
          </span>
          {notebook.updatedAt && notebook.updatedAt !== notebook.createdAt && (
            <span
              title={`Last edit: ${formatDate(notebook.updatedAt)} ${formatTime(notebook.updatedAt)}`}
            >
              Edited: {formatDate(notebook.updatedAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotebookCard;
